/**
 * @fileoverview NativeDropdown component for the Storyteller web application.
 * Displays a native dropdown menu with options to select from.
 *
 * @example
 * // Basic usage
 * <NativeDropdown options={[{ const: 'option1', title: 'Option 1' }]} onChange={() => {}} />
 */

import css from './NativeDropdown.module.css'

/**
 * @interface DropdownProps
 * @description Props for the NativeDropdown component.
 * @property {Function} onChange - A function to handle the change action.
 * @property {Object[]} options - The options to display in the dropdown.
 * @property {string} [placeholder] - The placeholder text for the dropdown.
 * @property {boolean} [disabled] - Whether the dropdown is disabled.
 * @property {string} [value] - The value of the selected option.
 * @property {string} path - The path of the dropdown.
 * @property {boolean} [required] - Whether the dropdown is required.
 */
interface DropdownProps extends React.HTMLAttributes<HTMLDivElement> {
    onChange: (value: unknown) => void
    options: { const?: string; title?: string }[]
    placeholder?: string
    disabled?: boolean
    value?: string
    path: string
    required?: boolean
    errors?: string
    hideErrorMessage?: boolean
}

/**
 * @function classNames
 * @description Helper function to concatenate class names with spaces.
 * @param {...string[]} classes - The class names to concatenate.
 * @returns {string} The concatenated class names.
 */
function classNames(...classes: string[]) {
    return classes.filter(Boolean).join(' ')
}

/**
 * @function NativeDropdown
 * @description Component for displaying a native dropdown menu with options to select from.
 * @param {DropdownProps} props - The props for the NativeDropdown component.
 * @returns {JSX.Element} The NativeDropdown component.
 */
export function NativeDropdown(props: DropdownProps) {
    const {
        options,
        disabled,
        hideErrorMessage,
        placeholder,
        value,
        errors = '',
        onChange,
        path,
        required,
    } = props
    const hasError = errors.length > 0

    return (
        <div className={classNames(css.container, props.className)}>
            <div>
                <select
                    name={path}
                    required={required}
                    id={path}
                    value={value}
                    className={classNames(
                        css.trigger,
                        value ? undefined : css.placeholder,
                        hasError && css.error
                    )}
                    disabled={disabled}
                    onChange={(e) => onChange(e.target.value)}
                >
                    {placeholder ? (
                        <option key="placeholder" value="" hidden>
                            {placeholder}
                        </option>
                    ) : null}
                    {options.map((option: { const: string; title: string }) => (
                        <option key={option.const} value={option.const}>
                            {option.title}
                        </option>
                    ))}
                </select>
                <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    className={css.chevron}
                >
                    <path
                        d="M19 9L12 16L5 9"
                        stroke="#737373"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    />
                </svg>
            </div>
            {hasError && !hideErrorMessage && <div className={css.errorMessage}>{errors}</div>}
        </div>
    )
}
